import ContentIllustration from '../../components/ui/ContentIllustration';
import { useState, useRef, useEffect } from 'react';
import { KeyRound, User, ArrowRight, Loader2, AlertTriangle, X, Mail } from 'lucide-react';
import usePageTitle from '../../hooks/usePageTitle';
import useQuizCodeStore from '../../store/quizCodeStore';
import QuizCodePlayer from '../../components/quiz-code/QuizCodePlayer';
import ThemeToggle from '../../components/ui/ThemeToggle';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';

export default function QuizCode() {
    usePageTitle('Quiz par code');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const { code: activeCode, sousSujet, design, finished, error: storeError, startQuiz, reset, loading, email: storeEmail, emailInvitation, participantEmail, emailLocked, mode: quizMode } = useQuizCodeStore();

    // Pre-fill code from URL (?code=XXXX-XXXX-XXXX-XXXX)
    const urlCode = searchParams.get('code') || '';
    // When arriving from a category-code hub, return_to points back to the hub so
    // "Quitter" / finish can bring the participant back.
    const returnTo = searchParams.get('return_to') || '';
    const [step, setStep] = useState('enter-code');
    const [codeInput, setCodeInput] = useState(() => {
        if (urlCode) return urlCode.toUpperCase();
        return '';
    });
    const [nameInput, setNameInput] = useState('');
    const [emailInput, setEmailInput] = useState('');
    const [localError, setLocalError] = useState(null);
    const [localLoading, setLocalLoading] = useState(false);
    const inputRefs = useRef([]);
    const autoSubmittedRef = useRef(false);

    // Auto-check code from URL on mount
    useEffect(() => {
        if (urlCode && urlCode.replace(/-/g, '').length >= 16 && !autoSubmittedRef.current) {
            autoSubmittedRef.current = true;
            handleCheckCode();
        }
    }, []);

    // Accepte les codes préfixés (PARS-…) ET les anciens sans préfixe.
    // On ne re-groupe plus par 4 (ça couperait le préfixe) : saisie gardée telle quelle.
    const formatCode = (raw) => {
        return raw.replace(/[^A-Za-z0-9-]/g, '').toUpperCase().slice(0, 28);
    };

    const handleCodeChange = (e) => {
        const formatted = formatCode(e.target.value);
        setCodeInput(formatted);
        setLocalError(null);
    };

    const handleCheckCode = async () => {
        const clean = codeInput.replace(/-/g, '');
        if (clean.length < 16) {
            setLocalError('Code invalide');
            return;
        }
        setLocalLoading(true);
        setLocalError(null);

        // Try to start without name — if IN_PROGRESS, resumes.
        // If AVAILABLE, returns a 'need-name' preview (no real start yet).
        const ok = await startQuiz(codeInput);
        setLocalLoading(false);

        if (ok === 'need-name') {
            // Pre-fill the optional email with whatever the owner invited with.
            const prefilled = useQuizCodeStore.getState().email || '';
            setEmailInput(prefilled);
            setStep('enter-name');
        } else if (ok) {
            const state = useQuizCodeStore.getState();
            if (state.questions.length > 0 && state.sousSujet) {
                // Resumed / review session
                setStep('quiz');
            }
        } else {
            setLocalError(useQuizCodeStore.getState().error || 'Code invalide');
        }
    };

    const handleStartWithName = async () => {
        if (!nameInput.trim()) {
            setLocalError('Entrez votre nom');
            return;
        }
        const email = emailInput.trim();
        if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
            setLocalError('Email invalide (ou laissez vide)');
            return;
        }
        setLocalLoading(true);
        setLocalError(null);

        const ok = await startQuiz(codeInput, nameInput.trim(), email || undefined);
        setLocalLoading(false);

        if (ok) {
            setStep('quiz');
        } else {
            setLocalError(useQuizCodeStore.getState().error || 'Erreur');
        }
    };

    const handleBack = () => {
        reset();
        // If we came from a hub (category-code), go back there instead of the code-entry screen
        if (returnTo) {
            navigate(returnTo);
            return;
        }
        setStep('enter-code');
        setCodeInput('');
        setNameInput('');
        setLocalError(null);
    };

    // === QUIZ MODE ===
    if (step === 'quiz' && activeCode) {
        const bgImage = design?.category_image || design?.header_image;
        return (
            <div className={`quiz-modal-overlay ${bgImage ? 'has-bg-image' : ''}`} style={bgImage ? { backgroundImage: `url(${bgImage})` } : {}}>
                <button className="quiz-modal-close quiz-modal-close--fixed" onClick={handleBack} title="Quitter">
                    <X size={22} strokeWidth={2} />
                </button>
                <ThemeToggle className="quiz-theme-toggle" />
                <QuizCodePlayer onBack={handleBack} hideEmailEditor={!!returnTo} />
            </div>
        );
    }

    // === HUB TRANSITION LOADER ===
    // Arriving from a category-code hub (return_to set) : show a full-screen loader
    // while startQuiz resolves so the participant doesn't see the "enter code" card flash.
    if (returnTo && urlCode && (localLoading || loading) && step === 'enter-code') {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--gradient-hero, linear-gradient(135deg, #0f0a2e, #1a1145))', color: '#fff', gap: 12 }}>
                <Loader2 size={28} className="spin" />
                <span style={{ fontSize: '1em', fontWeight: 500 }}>Chargement du quiz…</span>
            </div>
        );
    }

    // === ENTER CODE / ENTER NAME ===
    return (
        <div className="public-code-page">
            <div className="public-code-bg-overlay" />
            <ThemeToggle className="cc-theme-toggle" />
            <div className="public-code-inner">
                <h1 className="public-code-title">Accès à un quiz par code</h1>
                <div className="public-code-card">

                {step === 'enter-code' && (
                    <>
                        <div style={{ textAlign: 'center', marginBottom: 22 }}>
                            <div style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(124,58,237,0.12)', color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                                <ContentIllustration name="key-round" size={48} />
                            </div>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.9em', margin: 0 }}>
                                Entrez le code que l'on vous a fourni.
                            </p>
                        </div>

                        <input
                            type="text"
                            value={codeInput}
                            onChange={handleCodeChange}
                            onKeyDown={e => e.key === 'Enter' && handleCheckCode()}
                            placeholder="XXXX-XXXX-XXXX-XXXX"
                            style={{
                                width: '100%', padding: '14px 16px', fontSize: '1.2em', fontFamily: 'monospace',
                                textAlign: 'center', letterSpacing: 3, fontWeight: 700,
                                border: '2px solid var(--card-border)', borderRadius: 12,
                                background: 'var(--bg-subtle, #f8fafc)', color: 'var(--heading)',
                                outline: 'none', transition: 'border-color 0.2s',
                            }}
                            onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                            onBlur={e => e.target.style.borderColor = 'var(--card-border)'}
                            maxLength={28}
                            autoFocus
                        />

                        {localError && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, color: 'var(--danger)', fontSize: '0.85em' }}>
                                <AlertTriangle size={14} /> {localError}
                            </div>
                        )}

                        <button className="btn btn-primary" onClick={handleCheckCode}
                            disabled={localLoading || codeInput.replace(/-/g, '').length < 16}
                            style={{ width: '100%', marginTop: 20, padding: '14px', fontSize: '1.05em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                            {localLoading ? <Loader2 size={18} className="spin" /> : <><ArrowRight size={18} /> D&eacute;marrer</>}
                        </button>
                    </>
                )}

                {step === 'enter-name' && (
                    <>
                        <div style={{ textAlign: 'center', marginBottom: 22 }}>
                            <div style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(124,58,237,0.12)', color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                                <User size={28} />
                            </div>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.9em', margin: 0 }}>
                                Entrez votre nom pour commencer le quiz.
                            </p>
                        </div>

                        <input
                            type="text"
                            value={nameInput}
                            onChange={e => { setNameInput(e.target.value); setLocalError(null); }}
                            onKeyDown={e => e.key === 'Enter' && handleStartWithName()}
                            placeholder="Votre nom et pr&eacute;nom"
                            style={{
                                width: '100%', padding: '14px 16px', fontSize: '1.05em',
                                border: '2px solid var(--card-border)', borderRadius: 12,
                                background: 'var(--bg-subtle, #f8fafc)', color: 'var(--heading)',
                                outline: 'none', transition: 'border-color 0.2s',
                            }}
                            onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                            onBlur={e => e.target.style.borderColor = 'var(--card-border)'}
                            autoFocus
                        />

                        <div style={{ marginTop: 14, position: 'relative' }}>
                            <Mail size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                            <input
                                type="email"
                                value={emailInput}
                                onChange={e => { setEmailInput(e.target.value); setLocalError(null); }}
                                onKeyDown={e => e.key === 'Enter' && handleStartWithName()}
                                placeholder="Votre email (optionnel)"
                                style={{
                                    width: '100%', padding: '12px 14px 12px 40px', fontSize: '0.95em',
                                    border: '2px solid var(--card-border)', borderRadius: 12,
                                    background: 'var(--bg-subtle, #f8fafc)', color: 'var(--heading)',
                                    outline: 'none', transition: 'border-color 0.2s',
                                }}
                                onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                                onBlur={e => e.target.style.borderColor = 'var(--card-border)'}
                            />
                        </div>
                        <p style={{ fontSize: '0.75em', color: 'var(--text-muted)', margin: '6px 2px 0', lineHeight: 1.4 }}>
                            {participantEmail
                                ? <>Email enregistr&eacute; pr&eacute;c&eacute;demment &mdash; modifiable.</>
                                : emailInvitation
                                    ? <>Email fourni par l'inviteur &mdash; modifiable si vous souhaitez utiliser une autre adresse.</>
                                    : <>Fourni uniquement pour recevoir votre score{quizMode === 'sealed' && <> et &ecirc;tre pr&eacute;venu si les r&eacute;sultats sont d&eacute;verrouill&eacute;s</>}. Non partag&eacute;.</>
                            }
                        </p>
                        <p style={{ fontSize: '0.75em', color: 'var(--text-muted)', margin: '6px 2px 0', lineHeight: 1.4, fontStyle: 'italic' }}>
                            Vos r&eacute;sultats seront &eacute;galement transmis au fournisseur du code.
                        </p>

                        {localError && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, color: 'var(--danger)', fontSize: '0.85em' }}>
                                <AlertTriangle size={14} /> {localError}
                            </div>
                        )}

                        <button className="btn btn-primary" onClick={handleStartWithName}
                            disabled={localLoading || !nameInput.trim()}
                            style={{ width: '100%', marginTop: 20, padding: '14px', fontSize: '1.05em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                            {localLoading ? <Loader2 size={18} className="spin" /> : <><ArrowRight size={18} /> Commencer le quiz</>}
                        </button>

                        <button onClick={() => { setStep('enter-code'); setLocalError(null); }}
                            style={{ display: 'block', margin: '12px auto 0', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '0.85em', cursor: 'pointer' }}>
                            &larr; Changer de code
                        </button>
                    </>
                )}
                </div>
            </div>
            <footer className="public-code-footer">
                <nav className="public-code-footer-links">
                    <Link to="/mentions-legales">Mentions légales</Link>
                    <Link to="/cgv">CGV</Link>
                    <Link to="/confidentialite">Confidentialité</Link>
                    <a href="mailto:contact@monquiz.fr">Contact</a>
                </nav>
                <span className="public-code-footer-copy">© {new Date().getFullYear()} MonQuiz™ — Tous droits réservés</span>
            </footer>
        </div>
    );
}
